//*Libraries imports
import { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import { Visibility as VisibilityIcon } from "@mui/icons-material";
import { Calendar, Edit } from "lucide-react";

//*Hooks Imports
import { AppDispatch, RootState } from "@app/store";

//*Local Imports
import { fetchClients, deleteClient } from "./ClientSlice";
import { DataTable } from "@components/ui";
import type { Column } from "@components/ui/DataTable";
import { ClientOutputDTO } from "@features/Client/types";
import { LoadingSkeleton } from "@components/ui/LoadingSkeleton";
import { API_URL } from "@/config/api";
import { PhaseFormFieldType } from "@features/phaseForm";
import AgendaModal from "../user/AgendaModal";
import ClientViewModal from "./components/ClientViewModal";
import ClientDeleteDialog from "./components/ClientDeleteDialog";
import { IProduct } from "@features/Element/types";
import { DocumentOutputDTO } from "@features/document/types";
import { useNavigate } from "react-router-dom";

const ClientList = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { items, loading, error } = useSelector(
    (state: RootState) => state.client,
  );
  const navigate = useNavigate();

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const isLoadingRef = useRef<boolean>(false);
  const initialLoadDoneRef = useRef<boolean>(false);
  const [selectedClient, setSelectedClient] = useState<any>(null);

  const [productsIds, setProductsIds] = useState<Record<string, number>>({});
  const [products, setProducts] = useState<IProduct[]>([]);
  const [documents, setDocuments] = useState<DocumentOutputDTO[]>([]);

  const [meetings, setMeetings] = useState<any[]>([]);

  const [openModal, setOpenModal] = useState<boolean>(false);

  const loadData = useCallback(
    async (currentFilters: Record<string, string>) => {
      if (isLoadingRef.current) {
        return;
      }

      if (
        initialLoadDoneRef.current &&
        Object.keys(currentFilters).length === 0
      ) {
        return;
      }

      isLoadingRef.current = true;

      try {
        await dispatch(fetchClients(currentFilters));
        let products = await (
          await fetch(API_URL + "/products", {
            headers: {
              Authorization: localStorage.getItem("token"),
              Accept: "application/json",
            },
          })
        ).json();

        let documents = await (
          await fetch(API_URL + "/documents", {
            headers: {
              Authorization: localStorage.getItem("token"),
              Accept: "application/json",
            },
          })
        ).json();

        setProducts(products);
        setDocuments(documents);

        setIsInitialLoad(false);
        initialLoadDoneRef.current = true;
      } catch (error) {
        // Erro silencioso
      } finally {
        isLoadingRef.current = false;
      }
    },
    [dispatch],
  );

  const handleFilter = useCallback((newFilters: Record<string, string>) => {
    setFilters(newFilters);
  }, []);

  useEffect(() => {
    if (!initialLoadDoneRef.current) {
      loadData({});
    }
  }, [loadData]);

  useEffect(() => {
    if (initialLoadDoneRef.current && Object.keys(filters).length > 0) {
      loadData(filters);
    }
  }, [filters, loadData]);

  const handleDelete = async (id: string) => {
    try {
      await dispatch(deleteClient(id));
      setDeleteId(null);
      dispatch(fetchClients(filters));
    } catch (error) {
      console.error("❌ Erro ao excluir:", error);
    }
  };

  const filterFields = [
    {
      name: "name",
      label: "Nome",
      type: "text" as const,
    },
    {
      name: "email",
      label: "Email",
      type: "text" as const,
    },
  ];

  const columns: Column<ClientOutputDTO>[] = [
    {
      image: "im",
      key: "name",
      header: "Nome",
      render(_, item) {
        return item.name;
      },
    },
    {
      key: "Celular",
      header: "Celular",
      render(_, item) {
        return item.whatsapp;
      },
    },
    {
      key: "createdAt",
      header: "Criado em",
      render(_, item) {
        return item.createdAt
          ? new Date(item.createdAt).toLocaleString("pt-BR")
          : "N/A";
      },
    },
  ];

  if (isInitialLoad && loading) {
    return <LoadingSkeleton />;
  }

  return (
    <Box sx={{ p: 3 }}>
      {selectedClient != null && (
        <ClientViewModal
          selectedClient={selectedClient}
          setSelectedClient={setSelectedClient}
          products={products}
          documents={documents}
          productsIds={productsIds}
          loading={loading}
          error={error}
        />
      )}
      <AgendaModal
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        meetings={meetings}
      />

      <DataTable
        data={Array.isArray(items) ? items : items.data}
        columns={columns}
        newFormat
        basePath="clients"
        onDelete={(id) => {
          setDeleteId(id);
        }}
        hide_actions={false}
        title="Clientes"
        subtitle="Lista de clientes"
        loading={loading}
        error={error}
        hideEdit
        loadingComponent={<LoadingSkeleton title={false} filter={false} />}
        actions={[
          {
            label: "Editar",
            icon: <Edit />,
            onClick: async (item) => {
              navigate(
                                                `/clients/edit/${item._id || item.id || ""}`,
                                              )
            },
          },
          
          {
            label: "Abrir agenda",
            icon: <Calendar />,
            onClick: async (item) => {
              let schedules_profissional = await (
                await fetch(
                  API_URL +
                    "/appointments/schedules/all/all/" +
                    item._id +
                    "?details=all&type=client",
                  {
                    headers: {
                      Authorization: localStorage.getItem("token"),
                      Accept: "application/json",
                    },
                  },
                )
              ).json();
              setMeetings(schedules_profissional);
              setOpenModal(true);
            },
          },
          // {
          //   label: "Visualizar",
          //   icon: <VisibilityIcon />,
          //   onClick: async (item: any) => {
          //     let item2: any = { ...item };
          //     let proccess = await (
          //       await fetch(
          //         `${API_URL}/processes?clientId=${item._id}&whatsapp=${item.whatsapp}`,
          //         {
          //           headers: {
          //             Authorization: localStorage.getItem("token"),
          //             Accept: "application/json",
          //           },
          //         },
          //       )
          //     ).json();

          //     let clients_products = await (
          //       await fetch(
          //         API_URL +
          //           "/clients/get_client_products?client_id=" +
          //           item._id,
          //         {
          //           headers: {
          //             Authorization: localStorage.getItem("token"),
          //             Accept: "application/json",
          //           },
          //         },
          //       )
          //     ).json();

          //     let filled_forms: any = [];
          //     let products: any = {};
          //     let docsFilled: any = {};
          //     let phaseFormsIdFilled: any = {};

          //     proccess.map((item3: any) => {
          //       if (item3.productId) {
          //         products[item3.productId] = 1;
          //       }
          //       filled_forms.push(...item3.filledForms);
          //       item3.filledForms.map((item4: any) => {
          //         if (!item4.phaseForm && item4.phaseFormId) {
          //           item4.phaseForm = item4.phaseFormId;
          //         }
          //         phaseFormsIdFilled[item4.phaseForm] = item4;
          //         item4?.docs?.map((item5: any) => {
          //           docsFilled[item5.formFieldId] = item5;
          //           docsFilled[item5.formFieldId].name = documents.filter(
          //             (item10: DocumentOutputDTO) =>
          //               item5.formFieldId == item10._id,
          //           )[0]?.name;
          //         });
          //         item4.data.map((item5: any) => {
          //           if (
          //             item5.field.type == PhaseFormFieldType.BINDING_QUESTION
          //           ) {
          //             item5?.data?.map((item6: string) => {
          //               products[item6] = 1;
          //             });
          //           }
          //         });
          //       });
          //     });
          //     setProductsIds(products);
          //     item2.proccess = proccess;
          //     item2.filled_forms = filled_forms;
          //     item2.docsFilled = docsFilled;
          //     item2.phaseFormsIdFilled = phaseFormsIdFilled;
          //     item2.clients_products = clients_products?.data;

          //     setSelectedClient(item2);
          //   },
          // },
        ]}
      />

      <ClientDeleteDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId && handleDelete(deleteId)}
      />
    </Box>
  );
};

export default ClientList;
